import { useState, useEffect } from 'react';

/**
 * Detects whether the current device supports coarse pointer input (touch).
 * Returns `true` on iPad, tablets, and phones; `false` on desktop with a mouse.
 * Re-evaluates on media-query changes (e.g. Chrome DevTools device toggle).
 */
export function useIsTouchDevice(): boolean {
  const [isTouch, setIsTouch] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(pointer: coarse)').matches;
  });

  useEffect(() => {
    const mql = window.matchMedia('(pointer: coarse)');
    const handler = (e: MediaQueryListEvent) => setIsTouch(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return isTouch;
}

/**
 * Returns `true` when the viewport is tablet-sized (768px–1024px) or when
 * the device has a coarse pointer.  Used for tablet breakpoint decisions.
 */
export function useIsTablet(): boolean {
  const isTouch = useIsTouchDevice();
  const [isTabletWidth, setIsTabletWidth] = useState(() => {
    if (typeof window === 'undefined') return false;
    const w = window.innerWidth;
    return w >= 768 && w <= 1366;
  });

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px) and (max-width: 1366px)');
    const handler = (e: MediaQueryListEvent) => setIsTabletWidth(e.matches);
    mql.addEventListener('change', handler);
    setIsTabletWidth(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return isTouch && isTabletWidth;
}
